import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { aleatoire, desenvelopper, empreinteCle, envelopper } from './biometrie-crypto';
import { PinVaultService } from './pin-vault.service';

export type ResultatEnrolement =
  /** La copie est rangee dans le coffre. */
  | 'ok'
  /** Pas de biometrie ici : drapeau eteint, contexte non securise, pas d'authentificateur d'appareil, pas de coffre. */
  | 'indisponible'
  /** L'appareil sait faire de la biometrie mais pas l'extension PRF : rien n'est enregistre. */
  | 'prf-absent'
  /** La personne a refuse ou laisse expirer l'invite. */
  | 'annule'
  | 'erreur';

export type ResultatBiometrie =
  | { statut: 'ok'; cleBrute: string }
  /** Rien d'enregistre, ou biometrie indisponible. */
  | { statut: 'indisponible' }
  /** Invite refusee ou expiree : aucun message, la personne n'a rien fait de mal. */
  | { statut: 'annule' }
  /** Geste accompli mais cle non rouverte (autre doigt, copie alteree). */
  | { statut: 'echec' };

const TIMEOUT_MS = 60_000;
const ALGORITHMES: PublicKeyCredentialParameters[] = [
  { type: 'public-key', alg: -7 }, // ES256
  { type: 'public-key', alg: -257 }, // RS256
];

const estAnnulation = (e: unknown): boolean => {
  const nom = (e as { name?: string } | null)?.name;
  return nom === 'NotAllowedError' || nom === 'AbortError';
};

/**
 * LE DEVERROUILLAGE BIOMETRIQUE DU POSTE (WebAuthn + extension PRF).
 *
 * Verrouiller referme la cle de donnees ; seul le coffre, dechiffre par le NIP, la rend. Pour que la
 * biometrie puisse la rouvrir, elle doit y etre LIEE cryptographiquement — voir `biometrie-crypto.ts` :
 * la sortie PRF de l'authentificateur chiffre une copie de la cle, rangee dans le coffre.
 *
 * ── CE QUI EST VOLONTAIREMENT REFUSE ──
 * Sans PRF, pas de biometrie : on ne range jamais la cle sous une cle que tout script de l'origine peut
 * lire, la biometrie ne protegerait alors plus rien. Le NIP reste le seul chemin.
 *
 * ── CE QUI NE S'ECRIT QU'ICI ──
 * L'identifiant est lie au NOM D'HOTE : celui de dev ne vaut pas pour la prod, et il faut un contexte
 * securise (HTTPS ou localhost), comme pour le NIP.
 *
 * Desactive par defaut : `environment.deverrouillageBiometrique`.
 */
@Injectable({ providedIn: 'root' })
export class Biometrie {
  private readonly coffre = inject(PinVaultService);

  /** Le drapeau de configuration. Eteint, rien de ce service ne s'execute. */
  get activeParConfiguration(): boolean {
    return environment.deverrouillageBiometrique === true;
  }

  /**
   * Ce poste peut-il faire de la biometrie ? Drapeau allume, contexte securise et un authentificateur
   * d'appareil (empreinte, visage, PIN de l'appareil). La prise en charge de PRF, elle, ne se connait
   * qu'a l'enregistrement.
   */
  async disponible(): Promise<boolean> {
    if (!this.activeParConfiguration) return false;
    if (typeof window === 'undefined' || !window.isSecureContext) return false;
    if (typeof PublicKeyCredential === 'undefined' || !navigator.credentials) return false;
    try {
      return (await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable?.()) === true;
    } catch {
      return false;
    }
  }

  /** La biometrie est-elle enregistree pour cette personne ET utilisable ici ? Decide si la touche apparait. */
  async activePour(email: string): Promise<boolean> {
    if (!(await this.disponible())) return false;
    return (await this.coffre.lireBiometrie(email)) !== undefined;
  }

  /**
   * Enregistre la biometrie : cree un identifiant sur l'appareil, en tire une sortie PRF, y enveloppe la
   * cle de donnees et range la copie dans le coffre. Deux gestes biometriques : la creation, puis la
   * lecture de la sortie PRF (tous les authentificateurs ne la rendent pas des la creation).
   *
   * `cleBrute` vient du coffre ouvert par le NIP : on ne l'a jamais en memoire sans NIP.
   */
  async enregistrer(email: string, nomAffiche: string, cleBrute: string): Promise<ResultatEnrolement> {
    if (!(await this.disponible())) return 'indisponible';
    try {
      const creation = (await navigator.credentials.create({
        publicKey: {
          rp: { name: 'StockFlow POS' },
          user: { id: aleatoire(16), name: email, displayName: nomAffiche || email },
          challenge: aleatoire(32),
          pubKeyCredParams: ALGORITHMES,
          authenticatorSelection: { authenticatorAttachment: 'platform', userVerification: 'required', residentKey: 'discouraged' },
          timeout: TIMEOUT_MS,
          extensions: { prf: {} } as AuthenticationExtensionsClientInputs,
        },
      })) as PublicKeyCredential | null;
      if (!creation) return 'erreur';
      const prf = (creation.getClientExtensionResults() as { prf?: { enabled?: boolean } }).prf;
      if (prf?.enabled !== true) return 'prf-absent';

      const salt = aleatoire(32);
      const sortie = await this.lireSortiePrf(creation.rawId, salt);
      if (!sortie) return 'prf-absent';

      const { iv, cleChiffree } = await envelopper(sortie, cleBrute);
      await this.coffre.ecrireBiometrie(email, {
        credentialId: creation.rawId,
        salt: salt.buffer,
        iv,
        cleChiffree,
        empreinte: await empreinteCle(cleBrute),
        creeLe: Date.now(),
      });
      return 'ok';
    } catch (e) {
      return estAnnulation(e) ? 'annule' : 'erreur';
    }
  }

  /** Un geste biometrique rouvre la cle de donnees. Aucun essai du NIP n'est consomme, quoi qu'il arrive. */
  async deverrouiller(email: string): Promise<ResultatBiometrie> {
    if (!(await this.disponible())) return { statut: 'indisponible' };
    const blob = await this.coffre.lireBiometrie(email);
    if (!blob) return { statut: 'indisponible' };
    try {
      const sortie = await this.lireSortiePrf(blob.credentialId, new Uint8Array(blob.salt));
      if (!sortie) return { statut: 'echec' };
      return { statut: 'ok', cleBrute: await desenvelopper(sortie, blob.iv, blob.cleChiffree) };
    } catch (e) {
      return estAnnulation(e) ? { statut: 'annule' } : { statut: 'echec' };
    }
  }

  /** Retire la copie : la biometrie ne rouvre plus rien. Le NIP n'est pas touche. */
  async retirer(email: string): Promise<void> {
    await this.coffre.retirerBiometrie(email);
  }

  /** Un geste biometrique : la sortie PRF (32 octets) pour `salt`, ou `null` si l'appareil ne la rend pas. */
  private async lireSortiePrf(credentialId: ArrayBuffer, salt: Uint8Array): Promise<ArrayBuffer | null> {
    const assertion = (await navigator.credentials.get({
      publicKey: {
        challenge: aleatoire(32),
        allowCredentials: [{ type: 'public-key', id: credentialId }],
        userVerification: 'required',
        timeout: TIMEOUT_MS,
        extensions: { prf: { eval: { first: salt } } } as AuthenticationExtensionsClientInputs,
      },
    })) as PublicKeyCredential | null;
    const resultats = (assertion?.getClientExtensionResults() as { prf?: { results?: { first?: ArrayBuffer } } } | undefined)?.prf?.results;
    return resultats?.first ?? null;
  }
}
